// services/JobListingService.js
import { db } from "./firebase";
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  addDoc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  serverTimestamp,
} from "firebase/firestore";


class JobListingService {

  async createJobListing(organizationId, listingData) {
    try {
      if (!organizationId) {
        console.error("Missing organization ID for createJobListing");
        return null;
      }

      const response = await fetch(`https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${organizationId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(listingData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to create job listing');
      }

      return data.data.jobId;
    } catch (error) {
      console.error("Error creating job listing:", error);
      return null;
    }
  }

  async getJobListingDetails(orgId, jobId) {
    try {
      const response = await fetch(`https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${orgId}/${jobId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch job listing details')
      }

      return data
    } catch (error) {
      console.error('Error fetching job listing details:', error)
      throw error
    }
  }

 async updateIdealCandidateProfile(organizationId, listingId, idealCandidateProfile) {
    try {
      if (!organizationId || !listingId) {
        console.error("Missing required parameters for updateIdealCandidateProfile");
        return false;
      }

      if (!idealCandidateProfile || idealCandidateProfile.trim().length === 0) {
        console.error("Missing or empty idealCandidateProfile");
        return false;
      }

      const response = await fetch(`https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${organizationId}/${listingId}/ideal-candidate-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idealCandidateProfile })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to update ideal candidate profile');
      }

      return true;
    } catch (error) {
      console.error("Error updating ideal candidate profile:", error);
      return false;
    }
  }

  async updateJobListing(organizationId, listingId, listingData) {
    try {
      if (!organizationId || !listingId) {
        console.error("Missing required parameters for updateJobListing");
        return false;
      }

      const response = await fetch(`https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${organizationId}/${listingId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(listingData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to update job listing');
      }

      return true;
    } catch (error) {
      console.error("Error updating job listing:", error);
      return false;
    }
  }

  /**
   * Delete a job listing
   *
   * @param {string} organizationId - The organization ID
   * @param {string} listingId - The job listing ID
   * @returns {Promise<boolean>} - Whether the operation succeeded
   */
  async deleteJobListing(organizationId, listingId) {
    try {
      if (!organizationId || !listingId) {
        console.error("Missing required parameters for deleteJobListing");
        return false; 
      }

      // Delete the document
      const listingRef = doc(db, "organizations", organizationId, "jobListings", listingId);
      await deleteDoc(listingRef);

      return true;
    } catch (error) {
      console.error("Error deleting job listing:", error);
      return false;
    }
  }

  /**
   * Get all job listings for an organization
   *
   * @param {string} organizationId - The organization ID
   * @returns {Promise<Array>} - Array of job listing data
   */
  async getOrganizationJobListings(organizationId) {
    try {
      if (!organizationId) {
        console.error("Missing organization ID for getOrganizationJobListings");
        return [];
      }

      const listingsQuery = query(
        collection(db, "organizations", organizationId, "jobListings")
      );

      const querySnapshot = await getDocs(listingsQuery);

      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        createdAt: doc.data().createdAt?.toDate() || null
      }));

    } catch (error) {
      console.error("Error fetching organization job listings:", error);
      return [];
    }
  }

  async getJobsWithApplicants(organizationId) {
    try {
      if (!organizationId) {
        console.error("Missing organization ID for getOrganizationJobListings");
        return [];
      }

      const listingsQuery = query(
        collection(db, "organizations", organizationId, "jobListings")
      );

      const querySnapshot = await getDocs(listingsQuery);

      // Fetch applicants for each job listing
      const jobsWithApplicants = await Promise.all(
        querySnapshot.docs.map(async (jobDoc) => {
          const jobData = jobDoc.data();
          const jobId = jobDoc.id;

          // Fetch applicants from subcollection
          const applicantsRef = collection(
            db,
            "organizations",
            organizationId,
            "jobListings",
            jobId,
            "applicants"
          );

          const applicantsSnapshot = await getDocs(applicantsRef);

          const applicants = applicantsSnapshot.docs.map(applicantDoc => ({
            id: applicantDoc.id,
            ...applicantDoc.data()
          }));

          return {
            ...jobData,
            id: jobId,
            createdAt: jobData.createdAt?.toDate() || null,
            applicants: applicants,
            applicantCount: applicants.length
          };
        })
      );

      return jobsWithApplicants;

    } catch (error) {
      console.error("Error fetching organization job listings:", error);
      return [];
    }
  }

  /**
   * Get a specific job listing
   *
   * @param {string} organizationId - The organization ID
   * @param {string} listingId - The job listing ID
   * @returns {Promise<Object|null>} - Job listing data or null if not found
   */
  async getJobListing(organizationId, listingId) {
    try {
      if (!organizationId || !listingId) {
        console.error("Missing required parameters for getJobListing");
        return null;
      }

      const listingRef = doc(db, "organizations", organizationId, "jobListings", listingId);
      const listingDoc = await getDoc(listingRef);

      if (!listingDoc.exists()) {
        console.warn(`No job listing found with ID ${listingId}`);
        return null;
      }

      const data = listingDoc.data();
      
      return {
        ...data,
        id: listingDoc.id,
      };
    } catch (error) {
      console.error("Error fetching job listing:", error);
      return null;
    }
  }

  async updateApplicantStatus(orgId, listingId, applicantId, status) {
    try {
      if (!orgId || !listingId || !applicantId || !status) {
        console.error("Missing required parameters for updateApplicantStatus");
        return false;
      }

      // Reference to the applicant document in subcollection
      const applicantRef = doc(
        db,
        "organizations",
        orgId,
        "jobListings",
        listingId,
        "applicants",
        applicantId
      );

      // Check if applicant exists
      const applicantDoc = await getDoc(applicantRef);

      if (!applicantDoc.exists()) {
        console.error(`Applicant not found: ${applicantId} in job listing: ${listingId}`);
        return false;
      }

      // Update the applicant document with new status
      await updateDoc(applicantRef, {
        applicationStatus: status,
        updatedAt: new Date().toISOString()
      });

      console.log(`Successfully updated applicant ${applicantId} status to: ${status}`);
      return true;

    } catch (error) {
      console.error("Error updating applicant status:", error);
      return false;
    }
  }

}

export default new JobListingService();